import { Icon } from '@iconify/react'
import { Spin } from 'antd'
// import { IMAGE_CAPTURE_SETTINGS } from 'common/constants/imageCaptureConstants'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { CaptureButton } from '../capture-button/capture-button'

import styles from './camera-capture-modal.module.scss'

type Props = {
  active: boolean
  onCapture: (dataUrl: string) => void
  onCancel?: () => void
  hideActions?: boolean
  onReadyChange?: (ready: boolean) => void
  mirrored?: boolean
  shutter?: boolean
  facingMode?: 'user' | 'environment'
}

export type CameraCaptureHandle = {
  capture: () => void
}

// const {
//   JPEG_QUALITY: CAPTURE_JPEG_QUALITY,
//   CAMERA_IDEAL_WIDTH,
//   CAMERA_IDEAL_HEIGHT,
// } = IMAGE_CAPTURE_SETTINGS
const CAPTURE_JPEG_QUALITY = 0.9

const getCameraErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'name' in error) {
    const name = (error as { name?: string }).name
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      return 'Camera access was denied. Please allow camera access and try again.'
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      return 'No camera was found on this device.'
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
      return 'The camera is currently in use by another application.'
    }
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: string }).message
    if (typeof message === 'string' && message.length > 0) {
      return message
    }
  }
  return 'Unable to access the camera.'
}

export const CameraCapture = forwardRef<CameraCaptureHandle, Props>(
  (
    {
      active,
      onCapture,
      onCancel,
      hideActions = false,
      onReadyChange,
      mirrored = false,
      shutter = false,
      facingMode = 'environment',
    },
    ref,
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isReady, setIsReady] = useState(false)

    useEffect(() => {
      if (!active) {
        return
      }

      let cancelled = false
      setError(null)
      setIsReady(false)

      const startStream = async () => {
        if (!navigator.mediaDevices?.getUserMedia) {
          if (!cancelled) {
            setError('Camera is not supported in this browser.')
          }
          return
        }

        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            // video: {
            //   facingMode: { ideal: facingMode },
            //   width: { ideal: CAMERA_IDEAL_WIDTH },
            //   height: { ideal: CAMERA_IDEAL_HEIGHT },
            // },
            video: { facingMode: { ideal: facingMode } },
            audio: false,
          })

          if (cancelled) {
            stream.getTracks().forEach((track) => track.stop())
            return
          }

          streamRef.current = stream
          const videoElement = videoRef.current
          if (videoElement) {
            videoElement.srcObject = stream
            videoElement.onloadedmetadata = () => {
              videoElement.play().catch(() => {
                /* play() can reject if interrupted; ignore */
              })
              setIsReady(true)
            }
          }
        } catch (caughtError) {
          if (!cancelled) {
            setError(getCameraErrorMessage(caughtError))
          }
        }
      }

      void startStream()

      return () => {
        cancelled = true
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop())
          streamRef.current = null
        }
        setIsReady(false)
      }
    }, [active, facingMode])

    const handleCapture = useCallback(() => {
      const videoElement = videoRef.current
      if (!videoElement || !isReady) {
        return
      }

      const width = videoElement.videoWidth
      const height = videoElement.videoHeight
      if (width === 0 || height === 0) {
        return
      }

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        setError('Unable to capture frame from the camera.')
        return
      }

      // Mirror the captured frame to match the mirrored preview.
      if (mirrored) {
        ctx.translate(width, 0)
        ctx.scale(-1, 1)
      }
      ctx.drawImage(videoElement, 0, 0, width, height)
      const dataUrl = canvas.toDataURL('image/jpeg', CAPTURE_JPEG_QUALITY)
      onCapture(dataUrl)
    }, [isReady, onCapture, mirrored])

    const canCapture = isReady && !error

    useEffect(() => {
      onReadyChange?.(canCapture)
    }, [canCapture, onReadyChange])

    useImperativeHandle(ref, () => ({ capture: handleCapture }), [handleCapture])

    return (
      <div className={styles.body}>
        {error ? (
          <div className={styles.errorState}>
            <Icon icon='mdi:camera-off-outline' width={48} height={48} />
            <p>{error}</p>
          </div>
        ) : (
          <div className={styles.videoWrapper}>
            <video
              ref={videoRef}
              className={mirrored ? `${styles.video} ${styles.videoMirrored}` : styles.video}
              playsInline
              muted
            />
            {!isReady && (
              <div className={styles.loadingOverlay}>
                <Spin size='large' />
                <span>Starting camera...</span>
              </div>
            )}
            {shutter && <CaptureButton onClick={handleCapture} disabled={!isReady} />}
          </div>
        )}

        {!hideActions && (
          <div className={styles.actions}>
            {onCancel && (
              <button type='button' className={styles.secondaryButton} onClick={onCancel}>
                Cancel
              </button>
            )}
            {!error && (
              <button
                type='button'
                className={styles.captureButton}
                onClick={handleCapture}
                disabled={!isReady}>
                <Icon icon='mdi:camera' width={20} height={20} />
                <span>Capture</span>
              </button>
            )}
          </div>
        )}
      </div>
    )
  },
)

CameraCapture.displayName = 'CameraCapture'
